import axios from "@/widgets/shared/utils/axios-instance";
import { entityIdsOf, entityMapOf, onlyUnique } from "@/widgets/shared/utils/entity-utils";
import Constants from "@/widgets/shared/constants";

export const types = {
  LOAD_SESSIONS: "loadSessions",
  LOAD_SESSIONS_SUCCESS: "loadSessionsSuccess",
  LOAD_SESSIONS_ERROR: "loadSessionsError",
};

export function state() {
  return {
    ids: [],
    sessions: {},
    statuses: {},
    errors: {},
  };
}

export const mutations = {
  setSessions(state, sessions) {
    state.ids = [...entityIdsOf(sessions), ...state.ids].filter(onlyUnique);
    state.sessions = { ...state.sessions, ...entityMapOf(sessions) };
  },

  incrementFreePlacesForSession(state, sessionId) {
    const session = state.sessions[sessionId];
    if (session && session.agenda_session_tickets.length) {
      const ticket = session.agenda_session_tickets[0];
      if (Number.isInteger(ticket.tickets_left)) {
        const ticketsLeft = ticket.tickets_left + 1;
        state.sessions[sessionId].agenda_session_tickets[0].tickets_left = ticketsLeft;
      }
    }
  },

  decrementFreePlacesForSession(state, sessionId) {
    const session = state.sessions[sessionId];
    if (session && session.agenda_session_tickets.length) {
      const ticket = session.agenda_session_tickets[0];
      if (Number.isInteger(ticket.tickets_left)) {
        const ticketsLeft = ticket.tickets_left - 1;
        if (ticketsLeft >= 0) {
          state.sessions[sessionId].agenda_session_tickets[0].tickets_left = ticketsLeft;
        }
      }
    }
  },

  setFreePlacesForSession(state, { sessionId, freePlaces }) {
    const session = state.sessions[sessionId];
    if (session && session.agenda_session_tickets.length) {
      const ticket = session.agenda_session_tickets[0];
      if (ticket.tickets_left) {
        state.sessions[sessionId].agenda_session_tickets[0].tickets_left = freePlaces;
      }
    }
  },

  [types.LOAD_SESSIONS](state, componentId) {
    const statuses = { ...state.statuses };
    statuses[componentId] = Constants.STATUS_LOADING;
    state.statuses = statuses;
  },
  [types.LOAD_SESSIONS_SUCCESS](state, componentId) {
    const statuses = { ...state.statuses };
    statuses[componentId] = Constants.STATUS_LOADED;
    state.statuses = statuses;
  },
  [types.LOAD_SESSIONS_ERROR](state, { componentId, error }) {
    const statuses = { ...state.statuses };
    const errors = { ...state.errors };
    errors[componentId] = error;
    statuses[componentId] = Constants.STATUS_ERROR;
    state.statuses = statuses;
    state.errors = errors;
  },
  reset(state) {
    state.ids = [];
    state.sessions = {};
    state.statuses = {};
    state.errors = {};
  },
};

export const actions = {
  async loadSessions({ commit, rootState, state }, { componentId, force }) {
    if (state.statuses[componentId] !== Constants.STATUS_LOADED || force) {
      const eventId = rootState["eventId"];
      commit(types.LOAD_SESSIONS, componentId);
      await axios
        .get(`/events/${eventId}/components/${componentId}/agenda_sessions`)
        .then(result => {
          const sessions = result.data;
          commit("setSessions", sessions);
          commit(types.LOAD_SESSIONS_SUCCESS, componentId);
        })
        .catch(error => {
          if (error.response.status === 403 && error.response.data.errors[0].code === Constants.REST_AGENDA_SESSION_NO_ACCESS) {
            commit("setRequestRefreshToken", true, { root: true });
          } else {
            commit(types.LOAD_SESSIONS_ERROR, { componentId, error });
          }
        });
    }
  },
};

export const getters = {
  flattenSessions: state =>
    Object.values(state.sessions).reduce((acc, session) => {
      acc.push(session);
      acc.push(...session.agenda_session_children);
      return acc;
    }, []),

  sessionById: (state, getters) => id => getters.flattenSessions.find(session => session.id === id),

  allSessions(state, getters) {
    return state.ids.map(id => state.sessions[id]);
  },

  sessionsByComponent(state, getters) {
    return componentId => getters.allSessions.filter(session => session.event_component_id === componentId);
  },

  pathsFromCurrentComponent(state, getters, rootState) {
    const activeAgendaId = rootState.agendas.selectedId;
    return getters.allSessions
      .filter(session => session.event_component_id === activeAgendaId)
      .map(session => session.tag_agenda_path)
      .filter((val, index, self) => self.findIndex(element => element.id === val.id) === index)
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      });
  },

  placesFromCurrentComponent(state, getters, rootState) {
    const activeAgendaId = rootState.agendas.selectedId;
    return getters.allSessions
      .filter(session => session.event_component_id === activeAgendaId)
      .map(session => session.tag_agenda_place)
      .filter((val, index, self) => self.findIndex(element => element.id === val.id) === index)
      .sort((a, b) => {
        return a.value.localeCompare(b.value);
      });
  },

  daysFromCurrentComponent(state, getters, rootState, rootGetters) {
    const activeAgendaId = rootState.agendas.selectedId;
    if (activeAgendaId < 0) {
      return rootGetters["schedule/allSessions"]
        .map(session => session.agenda_session_day)
        .filter((val, index, self) => self.findIndex(element => element.id === val.id) === index);
    } else {
      return getters.allSessions
        .filter(session => session.event_component_id === activeAgendaId)
        .map(session => session.agenda_session_day)
        .filter((val, index, self) => self.findIndex(element => element.id === val.id) === index);
    }
  },

  visibleSessions(state, getters) {
    return (componentId, dayId) => getters["sessionsByComponent"](componentId).filter(session => session.agenda_session_day.id === dayId);
  },

  isLoadingSessionsFromComponent: state => componentId => state.statuses[componentId] === Constants.STATUS_LOADING,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
